import { Accordion, Box, Text, Image, VStack, DialogCloseTrigger } from '@chakra-ui/react';
import { useBreakpoint } from 'hooks/utils/layout/useBreakpoint';
import React, {
  ReactElement,
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import { Breakpoints } from 'types/breakpoints';
import { ModalImageVariantProps, SharedModalProps } from 'types/Modal';
import { Controlled as ZoomControlled } from 'react-medium-image-zoom';
import 'react-medium-image-zoom/dist/styles.css';
import QuickPinchZoom, { UpdateAction, make3dTransformValue } from 'react-quick-pinch-zoom';
import { CloseIcon } from 'assets/icons/v2';
import { CustomModal } from '../CustomModal';
import { CustomModalContent } from '../CustomModalContent';

type Props = SharedModalProps & ModalImageVariantProps;

export const ImageModal = ({
  handleClose,
  isShown,
  description,
  imgUrl,
  title,
}: Props): ReactElement => {
  const isDesktop = useBreakpoint(Breakpoints.DESKTOP);
  const imgRef = useRef<HTMLImageElement>(null);
  const [isRmizZoomed, setIsRmizZoomed] = useState(false);

  useEffect(() => {
    if (!isShown) {
      setIsRmizZoomed(false);
    }
  }, [isShown]);

  // Zag applies pointer-events: auto to persistentElements via waitForElement (async). Until
  // then, clicks pass through the portal and hit Chakra's backdrop → outside dismiss while
  // RMIZ stays zoomed (broken body scroll). Force auto as soon as the portal exists.
  useLayoutEffect(() => {
    if (!isShown || !isDesktop) return undefined;

    const ensureRmizPortalReceivesPointer = (): void => {
      const portal = document.querySelector('[data-rmiz-portal]');
      if (portal instanceof HTMLElement) {
        portal.style.pointerEvents = 'auto';
      }
    };

    ensureRmizPortalReceivesPointer();

    const observer = new MutationObserver(() => {
      ensureRmizPortalReceivesPointer();
    });
    observer.observe(document.body, { childList: true, subtree: true });

    return () => {
      observer.disconnect();
    };
  }, [isDesktop, isShown]);

  const onPinchToZoom = useCallback(({ x, y, scale }: UpdateAction) => {
    const { current: img } = imgRef;

    if (img) {
      const value = make3dTransformValue({ x, y, scale });
      img.style.setProperty('transform', value);
    }
  }, []);

  return (
    // eslint-disable-next-line react/jsx-no-useless-fragment
    <>
      {isDesktop ? (
        <CustomModal
          isOpen={isShown}
          onClose={handleClose}
          placement="center"
          size="md"
          closeOnInteractOutside={!isRmizZoomed}
        >
          <CustomModalContent p="30px 40px" scrollbar="hidden">
            <VStack width="100%" gap="15px">
              <ZoomControlled isZoomed={isRmizZoomed} onZoomChange={setIsRmizZoomed}>
                <Image src={imgUrl} />
              </ZoomControlled>

              <Text>Abb. {title}</Text>
              <Text>{description}</Text>
            </VStack>
          </CustomModalContent>
        </CustomModal>
      ) : (
        <CustomModal
          isOpen={isShown}
          onClose={handleClose}
          placement="center"
          borderRadius="0"
          hideHeader
          isFullscreen
          bg="background.quaternary"
          floatingCross
        >
          <DialogCloseTrigger color="text.onAccent" position="fixed">
            <CloseIcon boxSize="20px" color="text.onAccent" />
          </DialogCloseTrigger>
          <Box
            m="auto"
            position="fixed"
            width="100%"
            top="50%"
            left="50%"
            transform="translate(-50%, -50%)"
            overflow="hidden"
          >
            <QuickPinchZoom onUpdate={onPinchToZoom}>
              <Image src={imgUrl} ref={imgRef} />
            </QuickPinchZoom>
          </Box>
          <Box
            position="fixed"
            width="full"
            bg="background.quaternary"
            px="4"
            py="4"
            bottom="0"
            left="50%"
            transform="translate(-50%, 0%)"
          >
            <Accordion.Root collapsible color="text.onAccent">
              <Accordion.Item value="0" border="none">
                <Accordion.ItemTrigger>
                  <Text textAlign="left" py="2">
                    {title}
                  </Text>
                  <Accordion.ItemIndicator />
                </Accordion.ItemTrigger>
                <Accordion.ItemContent>
                  <Text>{description}</Text>
                </Accordion.ItemContent>
              </Accordion.Item>
            </Accordion.Root>
          </Box>
        </CustomModal>
      )}
    </>
  );
};
